import { PayloadAction } from '@reduxjs/toolkit';
import { CalculatedSlice } from './types';
import {
  REDUX_KEY_CALCULATED,
  SELECTED_VALUES,
  TVNZ_NETWORK
} from '../../constants';
import { DAY_LOWERCASE, MONTH_LOWERCASE } from '../../../constants/common';

export const updateBaseCPTReducer = (
  state: CalculatedSlice,
  action: PayloadAction<{ baseCPT: number; month: MONTH_LOWERCASE }>
) => {
  const { baseCPT, month } = action.payload;
  return {
    ...state,
    [month]: {
      ...state[month],
      baseCPT: baseCPT
    }
  };
};

export const updateBaseCPMReducer = (
  state: CalculatedSlice,
  action: PayloadAction<{ baseCPM: number; month: MONTH_LOWERCASE }>
) => {
  const { baseCPM, month } = action.payload;
  return {
    ...state,
    [month]: {
      ...state[month],
      baseCPM: baseCPM
    }
  };
};

export const updatePeakLoadingReducer = (
  state: CalculatedSlice,
  action: PayloadAction<{ peakLoading: number; month: MONTH_LOWERCASE }>
) => {
  const { peakLoading, month } = action.payload;
  return {
    ...state,
    [month]: {
      ...state[month],
      peakLoading: peakLoading
    }
  };
};

export const updateNetworkBreakdownPercentageReducer = (
  state: CalculatedSlice,
  action: PayloadAction<{
    networkBreakdownPercentage: number;
    month: MONTH_LOWERCASE;
    network: TVNZ_NETWORK;
  }>
) => {
  const { networkBreakdownPercentage, month, network } = action.payload;
  return {
    ...state,
    [month]: {
      ...state[month],
      [REDUX_KEY_CALCULATED.STATION_LOADING]: {
        ...state[month][REDUX_KEY_CALCULATED.STATION_LOADING],
        [network]: networkBreakdownPercentage
      }
    }
  };
};

export const updateDayLoadingPercentageReducer = (
  state: CalculatedSlice,
  action: PayloadAction<{
    dayLoadingPercentage: number;
    day: DAY_LOWERCASE;
    month: MONTH_LOWERCASE;
  }>
) => {
  const { dayLoadingPercentage, day, month } = action.payload;
  return {
    ...state,
    [month]: {
      ...state[month],
      [REDUX_KEY_CALCULATED.DAYS]: {
        ...state[month][REDUX_KEY_CALCULATED.DAYS],
        [day]: dayLoadingPercentage
      }
    }
  };
};

export const updateDayTotalLoadingPercentageRecucer = (
  state: CalculatedSlice,
  action: PayloadAction<{ totalLoading: number; month: MONTH_LOWERCASE }>
) => {
  const { totalLoading, month } = action.payload;
  return {
    ...state,
    [month]: {
      ...state[month],
      [REDUX_KEY_CALCULATED.DAYS]: {
        ...state[month][REDUX_KEY_CALCULATED.DAYS],
        loading: totalLoading
      }
    }
  };
};

export const updatePeakReducer = (
  state: CalculatedSlice,
  action: PayloadAction<{ peak: number; month: MONTH_LOWERCASE }>
) => {
  const { peak, month } = action.payload;
  return {
    ...state,
    [month]: {
      ...state[month],
      peak
    }
  };
};

export const updateTotalLoadingReducer = (
  state: CalculatedSlice,
  action: PayloadAction<{ totalLoading: number; month: MONTH_LOWERCASE }>
) => {
  const { totalLoading, month } = action.payload;
  return {
    ...state,
    [month]: {
      ...state[month],
      totalLoading: totalLoading
    }
  };
};

export const updateTotalCptReducer = (
  state: CalculatedSlice,
  action: PayloadAction<{ totalCpt: number; month: MONTH_LOWERCASE }>
) => {
  const { totalCpt, month } = action.payload;
  return {
    ...state,
    [month]: {
      ...state[month],
      totalCPT: totalCpt
    }
  };
};

export const updateTotalCpmReducer = (
  state: CalculatedSlice,
  action: PayloadAction<{ totalCpm: number; month: MONTH_LOWERCASE }>
) => {
  const { totalCpm, month } = action.payload;
  return {
    ...state,
    [month]: {
      ...state[month],
      totalCPM: totalCpm
    }
  };
};

export const updateStationSelectedValueReducer = (
  state: CalculatedSlice,
  action: PayloadAction<{
    stationSelectedValue: number;
    month: MONTH_LOWERCASE;
    network: TVNZ_NETWORK;
  }>
) => {
  const { stationSelectedValue, month, network } = action.payload;
  return {
    ...state,
    [month]: {
      ...state[month],
      [REDUX_KEY_CALCULATED.STATION_SELECTED_VALUES]: {
        ...state[month][REDUX_KEY_CALCULATED.STATION_SELECTED_VALUES],
        [network]: stationSelectedValue
      }
    }
  };
};

export const setIsStationOptimizedReducer = (
  state: CalculatedSlice,
  action: PayloadAction<{
    isOptimized: boolean;
    month: MONTH_LOWERCASE;
    network: TVNZ_NETWORK;
  }>
) => {
  const { isOptimized, month, network } = action.payload;
  return {
    ...state,
    [month]: {
      ...state[month],
      [REDUX_KEY_CALCULATED.STATION_SELECTED_OPTIMIZED]: {
        ...state[month][REDUX_KEY_CALCULATED.STATION_SELECTED_OPTIMIZED],
        [network]: isOptimized
      }
    }
  };
};

export const resetAllStationLoadingValuesReducer = (
  state: CalculatedSlice,
  action: PayloadAction<{ month: MONTH_LOWERCASE }>
) => {
  const { month } = action.payload;
  return {
    ...state,
    [month]: {
      ...state[month],
      [REDUX_KEY_CALCULATED.STATION_LOADING]: {
        ...state[month][REDUX_KEY_CALCULATED.STATION_LOADING],
        [TVNZ_NETWORK.ONE]: 0,
        [TVNZ_NETWORK.TWO]: 0,
        [TVNZ_NETWORK.DUKE]: 0,
        [TVNZ_NETWORK.LOADING]: 0
      },
      [REDUX_KEY_CALCULATED.STATION_SELECTED_VALUES]: {
        ...state[month][REDUX_KEY_CALCULATED.STATION_SELECTED_VALUES],
        [TVNZ_NETWORK.ONE]: 0,
        [TVNZ_NETWORK.TWO]: 0,
        [TVNZ_NETWORK.DUKE]: 0
      },
      [REDUX_KEY_CALCULATED.STATION_SELECTED_OPTIMIZED]: {
        ...state[month][REDUX_KEY_CALCULATED.STATION_SELECTED_OPTIMIZED],
        [TVNZ_NETWORK.ONE]: true,
        [TVNZ_NETWORK.TWO]: true,
        [TVNZ_NETWORK.DUKE]: true
      }
    }
  };
};

export const setFibLibValueReducer = (
  state: CalculatedSlice,
  action: PayloadAction<{ fibLib: number; month: MONTH_LOWERCASE }>
) => {
  const { fibLib, month } = action.payload;
  return {
    ...state,
    [month]: {
      ...state[month],
      [SELECTED_VALUES.FIBLIB]: fibLib
    }
  };
};

export const setFibLibLoadingPercentageReducer = (
  state: CalculatedSlice,
  action: PayloadAction<{
    fibLibLoadingPercentage: number;
    month: MONTH_LOWERCASE;
  }>
) => {
  const { fibLibLoadingPercentage, month } = action.payload;
  return {
    ...state,
    [month]: {
      ...state[month],
      [REDUX_KEY_CALCULATED.FIB_LIB_LOADING]: fibLibLoadingPercentage
    }
  };
};

export const toggleAdMirroringReducer = (
  state: CalculatedSlice,
  action: PayloadAction<{
    month: MONTH_LOWERCASE;
    isToggled: boolean;
  }>
) => {
  const { month, isToggled } = action.payload;
  const adMirroring = state[month][REDUX_KEY_CALCULATED.AD_MIRRORING];

  return {
    ...state,
    [month]: {
      ...state[month],
      [REDUX_KEY_CALCULATED.AD_MIRRORING]: {
        ...adMirroring,
        [REDUX_KEY_CALCULATED.IS_AD_MIRRORING_ENABLED]: isToggled
      }
    }
  };
};

export const setAdMirroringLoadingReducer = (
  state: CalculatedSlice,
  action: PayloadAction<{ adMirroringLoading: number; month: MONTH_LOWERCASE }>
) => {
  const { adMirroringLoading, month } = action.payload;
  const adMirroring = state[month][REDUX_KEY_CALCULATED.AD_MIRRORING];

  return {
    ...state,
    [month]: {
      ...state[month],
      [REDUX_KEY_CALCULATED.AD_MIRRORING]: {
        ...adMirroring,
        [REDUX_KEY_CALCULATED.AD_MIRRORING_LOADING]: adMirroringLoading
      }
    }
  };
};
